import { useMemo, useState } from 'react';

import { Frame, FrameOptionConsolidated } from '../../../../types/Products';
import { FramingChoice, GlazingOptionView, MountingOptionView } from '../types';
import {
  buildFrame,
  getFramePrice,
  getGlazingChoices,
  getMountingChoices,
  parseVariantTitle,
} from '../utils/framePricing';

interface UseFrameDraftArgs {
  frameOptions: FrameOptionConsolidated[];
  /** Confirmed selection to seed the draft from (a frame, or null/undefined for none). */
  currentSelection: Frame | null | undefined;
}

export interface FrameDraft {
  selectedStyleTitle: string | null;
  selectStyle: (styleTitle: string) => void;
  selectedGlazing: string | null;
  selectGlazing: (glazing: string) => void;
  selectedMounting: string | null;
  selectMounting: (mounting: string) => void;
  /** Glazing options for the selected style, with price deltas versus the current draft. */
  glazingOptions: GlazingOptionView[];
  /** Mounting options for the selected style and glazing, with price deltas versus the current draft. */
  mountingOptions: MountingOptionView[];
  /** The frame the draft currently resolves to (null when it can't resolve). */
  draftFrame: Frame | null;
  /** Total price of the draft frame. */
  framePrice: number;
  /** Abstract style+glazing+mounting choice to confirm (re-resolvable per product). */
  choice: FramingChoice;
}

/**
 * Owns the in-drawer working selection (frame style + glazing + mounting) for a
 * single product. Seeded from the current confirmed selection and derived purely
 * — no effect chains — so mounting the drawer fresh per product is enough to
 * reset it.
 */
export const useFrameDraft = ({
  currentSelection,
  frameOptions,
}: UseFrameDraftArgs): FrameDraft => {
  const seededFrame = currentSelection ?? null;

  // Seed once (lazy initialisers). The hook re-seeds by remounting per product.
  const [styleTitle, setStyleTitle] = useState<string | null>(() => {
    const option = seededFrame
      ? (frameOptions.find(({ title }) => title === seededFrame.title) ??
        frameOptions[0])
      : frameOptions[0];
    return option?.title ?? null;
  });

  const seedVariantTitle =
    seededFrame?.variant.title ?? frameOptions[0]?.variants[0]?.title;
  const seedParts = seedVariantTitle
    ? parseVariantTitle(seedVariantTitle)
    : null;

  const [glazing, setGlazing] = useState<string | null>(
    () => seedParts?.glazing ?? null,
  );
  const [mounting, setMounting] = useState<string | null>(
    () => seedParts?.mounting ?? null,
  );

  const selectedOption =
    frameOptions.find(({ title }) => title === styleTitle) ?? null;

  const glazingNames = useMemo(
    () =>
      getGlazingChoices(selectedOption).map((choice) => {
        return choice.glazing;
      }),
    [selectedOption],
  );

  // Keep glazing valid across style changes, preferring to preserve the name.
  const activeGlazing =
    glazingNames.find((name) => name === glazing) ?? glazingNames[0] ?? null;

  const mountingChoices = useMemo(
    () => getMountingChoices(selectedOption, activeGlazing),
    [selectedOption, activeGlazing],
  );

  const activeMountingChoice =
    mountingChoices.find((choice) => choice.mounting === mounting) ??
    mountingChoices[0] ??
    null;

  const glazingChoices = useMemo(
    () => getGlazingChoices(selectedOption, activeMountingChoice?.mounting),
    [selectedOption, activeMountingChoice?.mounting],
  );

  const activeGlazingChoice =
    glazingChoices.find((choice) => choice.glazing === activeGlazing) ??
    glazingChoices[0] ??
    null;

  const variantTitle =
    activeMountingChoice?.variantTitle ?? activeGlazingChoice?.variantTitle;

  const draftFrame =
    selectedOption && variantTitle
      ? buildFrame(selectedOption, variantTitle)
      : null;

  const framePrice = getFramePrice(draftFrame);

  const glazingOptions: GlazingOptionView[] = glazingChoices.map((choice) => ({
    ...choice,
    priceDelta: choice.total - framePrice,
  }));

  const mountingOptions: MountingOptionView[] = mountingChoices.map(
    (choice) => ({
      ...choice,
      priceDelta: choice.total - framePrice,
    }),
  );

  const choice: FramingChoice =
    selectedOption && activeGlazingChoice
      ? {
          styleTitle: selectedOption.title,
          glazing: activeGlazingChoice.glazing,
          ...(activeMountingChoice
            ? { mounting: activeMountingChoice.mounting }
            : {}),
        }
      : null;

  return {
    selectedStyleTitle: styleTitle,
    selectStyle: setStyleTitle,
    selectedGlazing: activeGlazingChoice?.glazing ?? null,
    selectGlazing: setGlazing,
    selectedMounting: activeMountingChoice?.mounting ?? null,
    selectMounting: setMounting,
    glazingOptions,
    mountingOptions,
    draftFrame,
    framePrice,
    choice,
  };
};
